import { styled } from '@abyss/web/tools/styled';
import { Button } from '@abyss/web/ui/Button';
import { Flex } from '@abyss/web/ui/Flex';
import { Text } from '@abyss/web/ui/Text';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../AdobeTagging/adobeLinkTrackEvent';
import { Constants } from '../Constants';
import { clearStyles } from './DisplayAZ.styled';

const ChipContainer = styled('div', {
  overflow: 'hidden',
  '.hide-checkbox input, .hide-checkbox input ~ svg': {
    position: 'absolute',
    left: '-10000px',
    top: 'auto',
    width: '1px',
    height: '1px',
  },
});

const ChipListContainer = styled('ul', {
  backgroundColor: '$primary2',
  display: 'flex',
  overflow: 'auto',
  overscrollBehaviorY: 'contain',
  maxWidth: '$pageSize',
  paddingBottom: '12px',
  paddingLeft: '0px',
  variants: {
    guestUser: {
      true: {
        paddingLeft: '5px',
      },
    },
  },
});

const chipTextStyles = {
  'abyss-chip-text': {
    padding: '0px',
    margin: '0px',
    width: '18.04px',
    height: '24px',
    fontSize: '20.25px',
    lineHeight: '24px',
    fontWeight: '700',
  },
};

const viewAllChipStyles = {
  'abyss-button-root': {
    height: 'auto',
    border: '1px solid $tint3',
    width: '100px',
    backgroundColor: '$primary2',
    padding: '$sm',
    fontWeight: 'bold',
    color: '$info1',
    margin: '0px',
    marginRight: '8px',
    marginTop: '$sm',
    cursor: 'pointer',
    justifyContent: 'center',
    lineHeight: '$sm',
    overflow: 'hidden',
    span: {
      color: '$uhcPrimary1 !important',
    },
    '&:hover': {
      backgroundColor: '$tint1',
    },
    '&:focus, &.active, &:hover:focus, &:focus-within': {
      backgroundColor: '$tint3',
    },
    textAlign: 'center',
  },
  'abyss-chip-text': {
    padding: '0px',
    margin: '0px',
    width: '53px',
    height: '20px',
    fontSize: '16px',
    lineHeight: '20px',
    fontWeight: '$semibold',
  },
};

const disabledChipStyles = {
  'abyss-button-root': {
    height: '43px',
    border: 'none',
    width: '34px',
    backgroundColor: '$primary2 !important',
    padding: '0px',
    fontWeight: '$bold',
    color: '$gray3 !important',
    margin: '0px',
    marginRight: '8px',
    marginTop: '$sm',
    cursor: 'default',
    justifyContent: 'center',
    '&:hover': {
      backgroundColor: 'white !important',
    },
    '&:focus, &.active, &:hover:focus, &:focus-within': {
      backgroundColor: '$primary2 !important',
    },
    textAlign: 'center',
  },
  ...chipTextStyles,
};

const chipStyles = (isGuestUser, isSelectedChip) => ({
  'abyss-button-root': {
    height: '43px',
    border: 'none',
    width: '34px',
    backgroundColor: '$primary2',
    padding: '0px',
    fontWeight: 'bold',
    color: '$info1',
    margin: '0px',
    marginRight: '8px',
    marginTop: '$sm',
    cursor: 'pointer',
    justifyContent: 'center',
    span: {
      color:
        isGuestUser && isSelectedChip
          ? '$primary1 !important'
          : '$info1 !important',
    },
    '&:hover': {
      backgroundColor: '$tint1',
    },
    '&:focus, &.active, &:hover:focus, &:focus-within': {
      backgroundColor: '$tint3',
      borderRadius: '100px',
    },
    textAlign: 'center',
  },
  ...chipTextStyles,
});

function ChipColor(chip) {
  return Constants.VIEW_ALL === chip ? '$primary1' : '$info1';
}

export interface DisableableChipObject {
  label: string;
  value: string;
  id: string;
  isDisabled?: boolean;
}

type Props = {
  setSelectedChip: (a: string) => void;
  selectedChip: string;
  chips: DisableableChipObject[];
  guestUser?: boolean;
};

export const BrowseAZ: React.FC<Props> = ({
  setSelectedChip,
  selectedChip,
  chips,
  guestUser = false,
}) => {
  const { t } = useTranslation();
  const [windowSize, setWindowSize] = useState<number>(window.innerWidth);
  const containerRef = useRef<HTMLDivElement>(null);
  const chipListRef = useRef<HTMLUListElement>(null);

  const onWheel = useCallback((e) => {
    if (chipListRef.current) {
      chipListRef.current.scrollTo({
        top: 0,
        left: chipListRef.current.scrollLeft + e.deltaY,
        behavior: 'auto',
      });
    }
  }, []);

  useEffect(() => {
    const handleWindowResize = () => {
      if (containerRef.current != null) {
        setWindowSize(containerRef.current.offsetWidth - 10);
      }
    };
    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  return (
    <ChipContainer ref={containerRef} role="radiogroup">
      <Text
        data-auto-testid="browse-az-label"
        fontWeight="500"
        id="browse-az-label"
        lineHeights={{ xs: '18px' }}
        size="14px"
      >
        {t('BROWSE_COMMON_SPECIALTIES.DIRECTORY')}
      </Text>
      <ChipListContainer
        aria-labelledby="browse-az-label"
        css={{ width: windowSize }}
        data-auto-testid="chip-list-container"
        data-testid="chip-list-container"
        guestUser={guestUser}
        onWheel={onWheel}
        ref={chipListRef}
      >
        {chips.map((item: DisableableChipObject, index: number) => (
          <li
            aria-checked={selectedChip === item.value}
            aria-disabled={item.isDisabled}
            key={index}
            role="radio"
          >
            <Flex
              className="hide-checkbox"
              data-auto-testid={`browse-${
                item.value === Constants.VIEW_ALL ? `${item.id}-` : ''
              }chip`}
              data-testid={`browse-${
                item.value === Constants.VIEW_ALL ? `${item.id}-` : ''
              }chip`}
              key={item.label}
            >
              {' '}
              <Button
                aria-pressed={selectedChip === item.value ? 'true' : 'false'}
                className={selectedChip === item.value ? 'active' : ''}
                css={
                  item.value === Constants.VIEW_ALL
                    ? viewAllChipStyles
                    : item.value === 'Clear'
                    ? clearStyles
                    : item.isDisabled
                    ? disabledChipStyles
                    : chipStyles(guestUser, selectedChip === item.value)
                }
                data-auto-testid={`az-chips-category-${item.id}-chip`}
                data-testid={`az-chips-category-${item.id}-chip`}
                isDisabled={item.isDisabled}
                key={index}
                onClick={() => {
                  if (!guestUser) {
                    adobeLinkTrackEvent({
                      name: `browse-common-specialities-${item?.label?.toLowerCase()}`,
                      location: `body:${Constants.BROWSE_COMMON_SPECIALTIES.CARD_HEADING.toLowerCase()}`,
                      type: 'internal',
                      destinationUrl: '',
                    });
                  }
                  if (!item.isDisabled) {
                    setSelectedChip(item.value);
                  }
                }}
                text={t(item.label)}
                variant="solid"
              >
                <Text
                  css={{
                    'abyss-text-root': {
                      justifyContent: 'center',
                      color: item.isDisabled ? '#CCC' : ChipColor(item.value),
                      padding: '0px',
                      margin: '0px',
                      width:
                        item.value === Constants.VIEW_ALL ? '100px' : '18.04px',
                      height:
                        item.value === Constants.VIEW_ALL || guestUser
                          ? 'auto'
                          : '24px',
                      fontSize:
                        item.value === Constants.VIEW_ALL || guestUser
                          ? '16px'
                          : '20.25px',
                      lineHeight:
                        item.value === Constants.VIEW_ALL || guestUser
                          ? '20px'
                          : '24px',
                      fontWeight:
                        item.value === Constants.VIEW_ALL || guestUser
                          ? '$semibold'
                          : '700',
                      marginRight: '0px',
                    },
                  }}
                >
                  {t(item.label)}
                </Text>
              </Button>
            </Flex>
          </li>
        ))}
      </ChipListContainer>
    </ChipContainer>
  );
};
